import { SchemaYellowHiveInsurance, SchemaYellowHiveProduct } from 'wg-api-sdk';
import { create } from 'zustand';

interface YellowhiveFlowsConfigureState {
  insurances: SchemaYellowHiveInsurance[];
  coverages: Record<string, string[]>;
  products: SchemaYellowHiveProduct[];

  // Actions
  setInsurances: (insurances: SchemaYellowHiveInsurance[]) => void;

  // Products actions
  setProducts: (products: SchemaYellowHiveProduct[]) => void;
  addProduct: (product: SchemaYellowHiveProduct) => void;
  removeProduct: (product: SchemaYellowHiveProduct) => void;
  // Coverage actions
  setCoverages: (insuranceId: string, coverages: string[]) => void;
  addCoverage: (insuranceId: string, coverage: string) => void;
  removeCoverage: (insuranceId: string, coverage: string) => void;
  // Reset
  resetStore: () => void;
}

export const useYellowhiveFlowsConfigureStore =
  create<YellowhiveFlowsConfigureState>((set) => {
    // Helper function to update array items in records
    const updateRecord = <T>(
      record: Record<string, T[]>,
      key: string,
      items: T[],
    ): Record<string, T[]> => ({
      ...record,
      [key]: items,
    });

    // Helper for adding an item if it doesn't already exist
    const addItem = <T>(
      record: Record<string, T[]>,
      key: string,
      item: T,
    ): Record<string, T[]> => {
      const existingItems = record[key] || [];
      if (existingItems.includes(item)) return record;

      return updateRecord(record, key, [...existingItems, item]);
    };

    // Helper for removing an item
    const removeItem = <T>(
      record: Record<string, T[]>,
      key: string,
      item: T,
    ): Record<string, T[]> => {
      const existingItems = record[key] || [];
      if (!existingItems.includes(item)) return record;

      return updateRecord(
        record,
        key,
        existingItems.filter((i) => i !== item),
      );
    };

    return {
      insurances: [],
      coverages: {},
      products: [],

      // Insurances
      setInsurances: (insurances) => set({ insurances }),

      // Coverages
      setCoverages: (insuranceId, coverages) =>
        set((state) => ({
          coverages: updateRecord(state.coverages, insuranceId, coverages),
        })),

      addCoverage: (insuranceId, coverage) =>
        set((state) => ({
          coverages: addItem(state.coverages, insuranceId, coverage),
        })),

      removeCoverage: (insuranceId, coverage) =>
        set((state) => ({
          coverages: removeItem(state.coverages, insuranceId, coverage),
        })),

      // Products
      setProducts: (products) => set({ products }),
      addProduct: (product) =>
        set((state) => ({ products: [...state.products, product] })),
      removeProduct: (product) =>
        set((state) => ({
          products: state.products.filter((p) => p.id !== product.id),
        })),
      // Reset
      resetStore: () => set({ insurances: [], coverages: {}, products: [] }),
    };
  });
